'use client';

import bannerImage from '@/assets/images/reviews/banner.png';
import mobileBannerImage from '@/assets/images/reviews/mobile-banner.png';
import { useScrollTopOnChange } from '@/common/hooks/useScrollTopOnChange';
import { fullPageLoadingIndicator } from '@/components/LoadingIndicator';
import Pagination from '@/components/Pagination';
import { useReviewsPage } from '@/hooks/ReviewsPage/useReviewsPage';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { Select } from 'antd';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import ReviewItem from './reviewItem';
import classes from './reviewsPage.module.css';

const ImagesViewerModal = React.lazy(() => import('@/components/ReviewList/imagesViewerModal'));

const ReviewsPage = props => {
  const { categories, filterCategory, filterCategoryName } = props;

  const talonProps = useReviewsPage(props);

  const { reviewdata, pageControl, loading, filterCategoryCount, pathname } = talonProps;

  useScrollTopOnChange(pageControl.currentPage);

  const router = useRouter();

  const { isMobile } = useIsMobile();

  const [reviewImages, setReviewImages] = useState([]);
  const [currentReviewImageIndex, setCurrentReviewImageIndex] = useState(0);
  const imageViewerRef = useRef(null);

  const banner = isMobile ? mobileBannerImage : bannerImage;

  const onItemImageClick = (images, index) => {
    const converted = images.map(item => {
      return {
        src: item.url.replace(/\/thumb_/, '/'),
      };
    });

    setReviewImages(converted);
    setCurrentReviewImageIndex(index);
    imageViewerRef?.current.setIsOpen(true);
  };

  const handleChange = id => {
    if (id === 2 || id === 21) {
      globalThis.open('/reviews');
    } else {
      const findCategory = categories.find(temp => temp.id === id);

      globalThis.open(`/reviews/category/${findCategory?.url_key}`);
    }
  };

  // 总数直接取接口的 total_count：各分类 count 相加会把同一条评价重复计数
  // （一条评价同时属于多个分类），累加结果约为真实数的 11 倍
  const totalCount = reviewdata?.total_count ?? 0;

  const count = useMemo(() => {
    if (pathname === '/reviews' || pathname === '/reviews/') {
      return totalCount;
    }

    return filterCategoryCount;
  }, [filterCategoryCount, pathname, totalCount]);

  const onItemClick = useCallback(
    item => {
      router.push(`/${item.product.url_key}.html`);
    },
    [router],
  );

  const maybePagination =
    reviewdata?.items.length > 0 ? (
      <div className={classes.pagination}>
        <Pagination pageControl={pageControl} />
      </div>
    ) : null;

  const reviewList = useMemo(() => {
    return reviewdata?.items
      .filter(temp => !!temp)
      .map((item, index) => (
        <ReviewItem
          key={index}
          item={item}
          onItemClick={onItemClick}
          onItemImageClick={onItemImageClick}
        />
      ));
  }, [onItemClick, reviewdata?.items]);

  if (loading) {
    return fullPageLoadingIndicator;
  }

  let options = categories
    .filter(temp => temp.id !== 21 && temp.id !== 2)
    .map(temp => {
      const count = reviewdata?.categories?.find(v => v.id === temp.id)?.count;

      return {
        value: temp.id,
        label: `${temp.name}(${count})`,
      };
    });

  options = [
    {
      label: `All Reviews (${totalCount.toLocaleString()} reviews)`,
      value: 2,
    },
    ...options,
  ];

  const categorySelect =
    isMobile && options.length > 0 ? (
      <Select
        className={classes.mobileSelect}
        defaultValue={filterCategory}
        onChange={handleChange}
        options={options}
      />
    ) : null;

  return (
    <>
      <div className='pb-[80px]'>
        <Image
          src={banner}
          className='w-full'
          alt=''
          priority
        />

        <div className='block bg-[#EFEFEF] p-[20px] lg_hidden'>
          <p className='font-poppins text-[30px] font-[700] uppercase text-title'>SEE WHAT OUR CUSTOMERS ARE SAYING </p>
          <p className='mt-[10px] font-sans text-title'>
            Discover how our bird feeders and houses are making backyards more joyful!
          </p>
        </div>

        <div className='flex w-full flex-col px-[15px] pt-[40px] lg_hidden'>
          <p className='font-poppins text-[24px] font-[600] text-title'>Reviews by Category</p>

          {categorySelect}
        </div>

        <div className='mx-auto w-[1440px] max-w-full px-[15px] pt-[40px] lg_px-0 lg_pt-[80px]'>
          <h1 className='text-left font-poppins text-[22px] font-[600] text-title lg_text-center lg_text-[30px]'>
            All Customer Reviews
          </h1>
          <div className='mt-[20px] flex gap-x-[40px] lg_mt-[40px]'>
            {!isMobile && (
              <div className='flex w-[340px] flex-col'>
                <h2 className='font-poppins text-[20px] font-[600] text-title'>Reviews by Category</h2>
                <ul className='mt-[40px] flex flex-col gap-y-[30px]'>
                  <li className={filterCategory === 0 ? classes.filterItemActive : classes.filterItem}>
                    <Link href='/reviews'>All Reviews ({totalCount.toLocaleString()} reviews)</Link>
                  </li>
                  {categories.map((item, index) => {
                    if (item.id === 2) {
                      return null;
                    }

                    const count = reviewdata?.categories?.find(temp => temp.id === item.id)?.count;

                    return (
                      <li key={index}>
                        <Link
                          className={filterCategory === item.id ? classes.filterItemActive : classes.filterItem}
                          href={`/reviews/category/${item.url_key}`}
                        >
                          {item.name + ' Reviews (' + count + ')'}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className={classes.reviewList}>
              <ul className='flex flex-col gap-y-[20px] lg_gap-y-[40px]'>{reviewList}</ul>

              {maybePagination}
            </div>
          </div>
        </div>
      </div>
      <Suspense fallback={null}>
        <div className={classes.imageList}>
          <ImagesViewerModal
            ref={imageViewerRef}
            images={reviewImages}
            index={currentReviewImageIndex}
          />
        </div>
      </Suspense>
    </>
  );
};

export default ReviewsPage;
